import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Eye, FileText, Download, Share2, Quote, Clock,
  Copy, Link as LinkIcon, Check,
} from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "0");

function totalViews(m) {
  return (m?.html_views || 0) + (m?.pdf_downloads || 0);
}

/** Fire-and-forget metric tracking. */
export function trackMetric(slug, event) {
  if (!slug) return;
  api.post(`/articles/${slug}/metrics`, { event }).catch(() => {});
}

/** Compact inline metrics shown on each listed article row. */
export function MetricsInline({ metrics }) {
  const { t } = useI18n();
  const m = metrics || {};
  const items = [
    { icon: Eye, value: totalViews(m), label: t("metrics.total_views") },
    { icon: Download, value: m.pdf_downloads || 0, label: t("metrics.pdf_downloads") },
    { icon: Share2, value: m.shares || 0, label: t("metrics.shares") },
  ];
  return (
    <div
      data-testid="metrics-inline"
      className="flex md:flex-col gap-3 md:gap-2 bg-[#F5F7FA] border border-gray-200 px-3 py-2 md:min-w-[120px]"
    >
      <div className="hidden md:block text-[9px] font-sans uppercase tracking-[0.18em] font-semibold text-[#2BA6A6] mb-0.5">
        {t("metrics.title")}
      </div>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-1.5 text-[#001F3F]" title={it.label}>
          <it.icon className="w-3.5 h-3.5 text-[#2BA6A6] shrink-0" />
          <span className="font-sans text-xs font-semibold tabular-nums">{fmt(it.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Full metrics panel for the article detail page. */
export function MetricsPanel({ metrics }) {
  const { t } = useI18n();
  const m = metrics || {};
  const rows = [
    { icon: Eye, label: t("metrics.total_views"), value: totalViews(m), strong: true },
    { icon: FileText, label: t("metrics.html_views"), value: m.html_views || 0 },
    { icon: Download, label: t("metrics.pdf_downloads"), value: m.pdf_downloads || 0 },
    { icon: Share2, label: t("metrics.shares"), value: m.shares || 0 },
    { icon: Quote, label: t("metrics.citations"), value: m.citations || 0 },
  ];
  const updated = m.updated_at ? new Date(m.updated_at).toLocaleDateString() : "—";
  return (
    <div data-testid="metrics-panel" className="border border-gray-200 bg-white">
      <div className="bg-[#001F3F] px-4 py-2.5 flex items-center gap-2">
        <Eye className="w-4 h-4 text-[#2BA6A6]" />
        <span className="font-sans text-[11px] uppercase tracking-[0.2em] font-semibold text-white">
          {t("metrics.title")}
        </span>
      </div>
      <div className="divide-y divide-gray-100">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2.5 text-gray-600">
              <r.icon className="w-4 h-4 text-[#2BA6A6]" />
              <span className="font-sans text-xs">{r.label}</span>
            </div>
            <span
              data-testid={`metric-${r.label}`}
              className={`font-sans tabular-nums ${r.strong ? "text-base font-bold text-[#001F3F]" : "text-sm font-semibold text-[#001F3F]"}`}
            >
              {fmt(r.value)}
            </span>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 bg-[#F5F7FA] flex items-center gap-1.5 text-[10px] text-gray-500 font-sans">
        <Clock className="w-3 h-3 text-[#2BA6A6]" />
        {t("metrics.last_updated")}: {updated}
      </div>
    </div>
  );
}

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.587 5.299l-.999 3.648 3.911-1.026zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const networks = [
  {
    key: "whatsapp", labelKey: "share.whatsapp", Icon: WhatsAppIcon,
    color: "#25D366",
    href: ({ text, url }) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    key: "facebook", labelKey: "share.facebook", Icon: null,
    color: "#1877F2",
    href: ({ text, url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    key: "twitter", labelKey: "share.twitter", Icon: null,
    color: "#000000",
    href: ({ text, url }) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    key: "linkedin", labelKey: "share.linkedin", Icon: null,
    color: "#0A66C2",
    href: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

const FbIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>);
const XIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>);
const LinkedInIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" /></svg>);
const NETWORK_ICONS = { facebook: FbIcon, twitter: XIcon, linkedin: LinkedInIcon };

/** Share button + dropdown for the article detail page. */
export function ShareMenu({ article, slug }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `${t("share.text")}: "${article?.title || ""}"`;

  const onShare = (net) => {
    trackMetric(slug, "share");
    window.open(net.href({ text, url }), "_blank", "noopener,noreferrer,width=640,height=640");
    setOpen(false);
  };

  const onNative = async () => {
    trackMetric(slug, "share");
    setOpen(false);
    const shareData = { title: article?.title, text, url };
    try {
      if (article?.visual_abstract_url && navigator.canShare) {
        const resp = await fetch(assetUrl(article.visual_abstract_url));
        const blob = await resp.blob();
        const file = new File([blob], "visual-abstract.png", { type: blob.type || "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ ...shareData, files: [file] });
          return;
        }
      }
      await navigator.share(shareData);
    } catch (_) { /* user cancelled or unsupported */ }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share.copied"));
    } catch (_) {
      toast.error("Could not copy link");
    }
    trackMetric(slug, "share");
    setOpen(false);
  };

  const hasNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => (hasNative ? onNative() : setOpen((o) => !o))}
        onContextMenu={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        data-testid="share-button"
        className="w-full flex items-center justify-center gap-2.5 border border-[#001F3F] text-[#001F3F] px-5 py-3 text-sm font-sans uppercase tracking-[0.15em] font-semibold hover:bg-[#001F3F] hover:text-white transition-colors"
      >
        <Share2 className="w-4 h-4" /> {t("share.button")}
      </button>

      {open && (
        <div
          data-testid="share-menu"
          className="absolute right-0 z-30 mt-2 w-56 bg-white border border-gray-200 shadow-xl py-1"
        >
          {networks.map((net) => {
            const Icon = net.Icon || NETWORK_ICONS[net.key];
            return (
              <button
                key={net.key}
                type="button"
                onClick={() => onShare(net)}
                data-testid={`share-${net.key}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans text-[#001F3F] hover:bg-[#F5F7FA] transition-colors"
              >
                <Icon className="w-4 h-4" style={{ color: net.color }} />
                {t(net.labelKey)}
              </button>
            );
          })}
          <button
            type="button"
            onClick={onCopy}
            data-testid="share-copy"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans text-[#001F3F] hover:bg-[#F5F7FA] transition-colors border-t border-gray-100"
          >
            <LinkIcon className="w-4 h-4 text-[#2BA6A6]" />
            {t("share.copy")}
          </button>
        </div>
      )}
    </div>
  );
}
